import React, { Component } from "react";
import {
    Row,
    Col,
    FormGroup,
    ControlLabel,
    FormControl,
    Alert,
} from "react-bootstrap";
import moment from "moment";
import { Card } from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import DatePicker from "react-datepicker";

import { createFormRequest, editFormRequest, previewLeaveRequest } from "../../api/form";
import { EmployeeAsyncSelect } from "components/EmployeeAsyncSelect/EmployeeAsyncSelect";
import { fetchEmployeeLeaveBalanceRequest } from "../../api/employee";

import LeaveAllocationEditor from "components/LeaveAllocations/LeaveAllocationEditor";

const PAID_LEAVE_TYPES = [
    { value: "sickLeave", label: "Sick Leave" },
    { value: "vacationLeave", label: "Vacation Leave" },
    { value: "losLeave", label: "LOS Leave" },
];

const UNPAID_LEAVE_TYPES = [
    ...PAID_LEAVE_TYPES,
    { value: "other", label: "Other" },
];

const isPaidLeaveEligible = (employee) => Boolean(
    employee &&
    employee.employmentStatus !== "Probationary" &&
    employee.dateRegularized &&
    moment(employee.dateRegularized, "YYYY-MM-DD", true).isValid()
);

class LeaveForm extends Component {
    constructor(props) {
        super(props);
        this.previewVersion = 0;
        this.balanceVersion = 0;
        this.state = {
            allocationMode: false,
            allocationPreview: null,
            previewLoading: false,
            previewError: null,
            saving: false,
            resource: {
                employee: null,
                applicationType: props.applicationType || null,
                paymentType: null,
                leaveType: null,
                leaveTypeDetail: null,
                leaveAllocations: [],
                startDate: null,
                endDate: null,
                date: null,
                timeFrom: null,
                timeTo: null,
                reason: null,
                approvedBy: null,
            },
            employeeSelectOptions: [],
            formErrors: null,
            leaveBalance: null,
            selectedEmployee: null,
            balanceLoading: false,
            balanceError: null,
            error: null,
        };
    }

    invalidatePreview = () => {
        this.previewVersion += 1;
        this.setState({ allocationPreview: null, previewError: null, previewLoading: false });
    };

    componentWillUnmount() {
        this.previewVersion += 1;
        this.balanceVersion += 1;
    }

    setAllocations = (leaveAllocations) => {
        this.invalidatePreview();
        this.setState(prev => ({ resource: { ...prev.resource, leaveAllocations } }));
    };

    toggleAllocations = () => {
        this.invalidatePreview();
        this.setState(prev => ({
            allocationMode: !prev.allocationMode,
            resource: {
                ...prev.resource,
                leaveAllocations: prev.allocationMode ? [] : [{ leaveType: prev.resource.leaveType || "", days: "" }],
            },
        }));
    };

    allocationPayload = () => {
        const { employee, startDate, endDate, leaveAllocations } = this.state.resource;
        return { employee, startDate, endDate, leaveAllocations: leaveAllocations.map(row => ({ ...row, days: Number(row.days) })) };
    };

    previewAllocations = () => {
        const { employee, startDate, endDate } = this.state.resource;
        if (!employee || !startDate || !endDate) {
            this.setState({ previewError: "Select an employee, start date, and end date first." });
            return;
        }
        const version = ++this.previewVersion;
        this.setState({ previewLoading: true, allocationPreview: null, previewError: null });
        previewLeaveRequest(this.allocationPayload(), result => {
            if (version === this.previewVersion) this.setState({ allocationPreview: result, previewLoading: false });
        }, error => {
            if (version === this.previewVersion) this.setState({ previewError: error.message || "Could not calculate the preview.", previewLoading: false });
        });
    };

    componentDidMount() {
        // employeeSelectOptionRequest(
        //     (result) => this.setState({ employeeSelectOptions: result }),
        //     (error) => this.setState({ error: "There was an error" }),
        // );
    }

    handleFormChange = (e) => {
        const { target } = e;
        const { name, value } = target;
        if (["paymentType", "applicationType", "leaveType"].includes(name)) this.invalidatePreview();

        this.setState(
            (prevState) => {
                const resource = {
                    ...prevState.resource,
                    [name]: value,
                };

                if (name === "applicationType" && value !== "leave") {
                    resource.paymentType = null;
                    resource.leaveType = null;
                }

                if (name === "paymentType") {
                    resource.leaveType = null;
                    resource.leaveTypeDetail = null;
                    resource.leaveAllocations = [];
                }

                return {
                    resource,
                    allocationMode: name === "paymentType" || name === "applicationType" ? false : prevState.allocationMode,
                    error: null,
                };
            },
            () => {
                if (name === "paymentType" && value === "withPay") {
                    this.fetchLeaveBalance();
                }
            },
        );
    };

    onChangeDate = (date, field) => {
        this.invalidatePreview();
        let value = date ? moment(date).format("YYYY-MM-DD") : null;

        this.setState(
            (prevState) => ({
                resource: {
                    ...prevState.resource,
                    [field]: value,
                },
                error: null,
            }),
            () => {
                if (this.state.resource.paymentType === "withPay") {
                    this.fetchLeaveBalance();
                }
            },
        );
    };

    fetchLeaveBalance = () => {
        const version = ++this.balanceVersion;
        const { employee, startDate, endDate } = this.state.resource;
        if (!employee) {
            return;
        }

        const balanceDate = endDate || startDate;
        const year = balanceDate
            ? moment(balanceDate, "YYYY-MM-DD").year()
            : moment().year();

        this.setState({
            balanceLoading: true,
            leaveBalance: null,
            balanceError: null,
        });
        fetchEmployeeLeaveBalanceRequest(
            employee,
            year,
            (result) => version === this.balanceVersion &&
                this.setState({
                    leaveBalance: result,
                    balanceLoading: false,
                    balanceError: null,
                }),
            (error) => version === this.balanceVersion &&
                this.setState({
                    leaveBalance: null,
                    balanceLoading: false,
                    balanceError:
                        error.message ||
                        "Leave balances could not be loaded.",
                }),
            balanceDate,
        );
    };

    errorMessage = (name, message) => {
        const { formErrors } = this.state;

        return (
            <>
                {formErrors && formErrors[name] && (
                    <div className="form-text text-danger sz-11px">
                        {message}
                    </div>
                )}
            </>
        );
    };

    validate = () => {
        if (this.state.saving) return;
        if (this.state.allocationMode && !this.state.allocationPreview) {
            this.setState({ previewError: "Preview the dates and balances before submitting." });
            return;
        }
        const {
            employee,
            applicationType,
            paymentType,
            leaveType,
            startDate,
            endDate,
            date,
            timeFrom,
            timeTo,
            approvedBy,
        } = this.state.resource;

        const errors = {};
        if (!employee) {
            errors.employee = true;
        }

        if (!applicationType) {
            errors.applicationType = true;
        }

        if (applicationType && applicationType === "leave") {
            if (!paymentType) {
                errors.paymentType = true;
            }

            if (!leaveType && !this.state.allocationMode) {
                errors.leaveType = true;
            }

            if (!startDate) {
                errors.startDate = true;
            }

            if (!endDate) {
                errors.endDate = true;
            }

            if (
                startDate &&
                endDate &&
                moment(endDate).isBefore(moment(startDate), "day")
            ) {
                errors.endDateBeforeStart = true;
            }

            const selectedEmployee = this.state.selectedEmployee;
            if (paymentType === "withPay" && !isPaidLeaveEligible(selectedEmployee)) {
                errors.paidLeaveEligibility = true;
            }
            const dateRegularized = moment(
                selectedEmployee && selectedEmployee.dateRegularized,
                "YYYY-MM-DD",
                true,
            );
            if (
                startDate &&
                dateRegularized.isValid() &&
                moment(startDate, "YYYY-MM-DD", true).isBefore(
                    dateRegularized,
                    "day",
                )
            ) {
                errors.leaveBeforeRegularization = true;
            }

            if (
                paymentType === "withPay" &&
                !this.state.allocationMode &&
                this.state.leaveBalance &&
                (
                    (
                        leaveType &&
                        this.state.leaveBalance.balances[leaveType] &&
                        this.state.leaveBalance.balances[leaveType]
                            .availableToRequest <= 0
                    ) ||
                    Object.values(this.state.leaveBalance.balances).every(
                        (balance) => balance.availableToRequest <= 0,
                    )
                )
            ) {
                errors.leaveBalance = true;
            }
        }

        if (applicationType && applicationType === "undertime") {
            if (!date) {
                errors.date = true;
            }

            if (!timeFrom) {
                errors.timeFrom = true;
            }

            if (!timeTo) {
                errors.timeTo = true;
            }
        }

        if (!approvedBy) {
            errors.approvedBy = true;
        }

        if (Object.keys(errors).length > 0) {
            this.setState({ formErrors: errors });
        } else {
            this.setState({ formErrors: null });
            this.save();
        }
    };

    save = () => {
        this.setState({ saving: true });
        const {
            employee,
            applicationType,
            paymentType,
            leaveType,
            leaveTypeDetail,
            startDate,
            endDate,
            date,
            timeFrom,
            timeTo,
            reason,
            approvedBy,
        } = this.state.resource;

        const { instanceId } = this.props;

        const isNewRecord = !instanceId;

        let data = {
            employee,
            formType: "leave-undertime",
            applicationType,
            paymentType,
            leaveType,
            leaveTypeDetail,
            startDate,
            endDate,
            date,
            timeFrom,
            timeTo,
            reason,
            approvedBy,
        };

        if (this.state.allocationMode) {
            data.leaveType = "mixed";
            data.leaveAllocations = this.allocationPayload().leaveAllocations;
        }

        if (isNewRecord) {
            createFormRequest(
                data,
                (result) => this.saveSuccess(),
                (error) => this.saveFailure(error),
            );
        } else {
            editFormRequest(
                instanceId,
                data,
                (result) => this.saveSuccess(),
                (error) => this.saveFailure(error),
            );
        }
    };

    saveSuccess = () => {
        const { employee } = this.state.resource;

        this.setState({ error: null, saving: false });
        this.props.goToList(employee);
    };

    saveFailure = (error) => {
        this.invalidatePreview();
        this.setState({
            saving: false,
            error:
                error.message ||
                "There was an error saving the leave request.",
        });
    };

    handleChange = (employeeValue) => {
        this.invalidatePreview();
        this.balanceVersion += 1;
        this.setState(
            (prevState) => ({
                resource: {
                    ...prevState.resource,
                    employee: employeeValue ? employeeValue.value : null,
                    paymentType:
                        prevState.resource.paymentType === "withPay" &&
                        !isPaidLeaveEligible(employeeValue && employeeValue.data)
                            ? null
                            : prevState.resource.paymentType,
                    leaveType: null,
                    leaveAllocations: [],
                },
                allocationMode: false,
                balanceLoading: false,
                leaveBalance: null,
                selectedEmployee: employeeValue ? employeeValue.data : null,
                balanceError: null,
                error: null,
            }),
            () => {
                if (
                    employeeValue &&
                    this.state.resource.paymentType === "withPay"
                ) {
                    this.fetchLeaveBalance();
                }
            },
        );
    };

    handleApproveChange = (approveValue) => {
        this.setState((prevState) => ({
            resource: {
                ...prevState.resource,
                approvedBy: approveValue.value,
            },
        }));
    };

    render() {
        const isApplicationTypeFixed = Boolean(this.props.applicationType);
        const {
            applicationType,
            paymentType,
            leaveType,
            leaveTypeDetail,
            timeFrom,
            timeTo,
            reason,
        } = this.state.resource;
        const {
            leaveBalance,
            balanceLoading,
            balanceError,
            error,
        } = this.state;
        const selectedBalance =
            !this.state.allocationMode &&
            leaveBalance &&
            leaveBalance.balances &&
            leaveBalance.balances[leaveType];
        const hasAnyPaidBalance =
            leaveBalance &&
            leaveBalance.balances &&
            PAID_LEAVE_TYPES.some(
                (type) =>
                    leaveBalance.balances[type.value] &&
                    leaveBalance.balances[type.value].availableToRequest > 0,
            );
        const selectedEmployee = this.state.selectedEmployee;
        const paidLeaveEligible = isPaidLeaveEligible(selectedEmployee);
        const regularizationDate = moment(
            selectedEmployee && selectedEmployee.dateRegularized,
            "YYYY-MM-DD",
            true,
        );
        const minimumLeaveDate = regularizationDate.isValid()
            ? regularizationDate.toDate()
            : undefined;

        const startDate = this.state.resource.startDate
            ? moment(this.state.resource.startDate, "YYYY-MM-DD").toDate()
            : "";
        const endDate = this.state.resource.endDate
            ? moment(this.state.resource.endDate, "YYYY-MM-DD").toDate()
            : "";
        const date = this.state.resource.date
            ? moment(this.state.resource.date, "YYYY-MM-DD").toDate()
            : "";

        return (
            <Row>
                <Col md={12}>
                    <Card
                        title={
                            applicationType === "undertime"
                                ? "Undertime Form"
                                : "Leave Form"
                        }
                        content={
                            <form onSubmit={event => { event.preventDefault(); this.validate(); }}>
                                <FormGroup>
                                    <ControlLabel>
                                        Employee
                                        <span className="required-star">*</span>
                                    </ControlLabel>
                                    <EmployeeAsyncSelect
                                        onChange={this.handleChange}
                                    />
                                    {/* <FormControl 
                        onChange={this.handleFormChange}
                        componentClass="select" 
                        name="employee"
                        value={employee}
                      >
                      <option value="">Select</option>
                      {employeeSelectOptions.map(option => {
                          return (
                            <option
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </option>
                          );
                      })}
                    </FormControl> */}
                                    {this.errorMessage(
                                        "employee",
                                        "Employee can't be empty",
                                    )}
                                </FormGroup>
                                {!isApplicationTypeFixed && (
                                    <FormGroup>
                                        <ControlLabel>
                                            Application Type
                                            <span className="required-star">
                                                *
                                            </span>
                                        </ControlLabel>
                                        <FormControl
                                            onChange={this.handleFormChange}
                                            componentClass="select"
                                            name="applicationType"
                                            value={applicationType}
                                        >
                                            <option value="">Select</option>
                                            <option value="leave">
                                                Leave
                                            </option>
                                            <option value="undertime">
                                                Undertime
                                            </option>
                                        </FormControl>
                                        {this.errorMessage(
                                            "applicationType",
                                            "Application Type can't be empty",
                                        )}
                                    </FormGroup>
                                )}
                                {applicationType === "leave" && (
                                    <div>
                                        <FormGroup>
                                            <ControlLabel>
                                                Payment Type
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </ControlLabel>
                                            <FormControl
                                                onChange={this.handleFormChange}
                                                componentClass="select"
                                                name="paymentType"
                                                value={paymentType}
                                            >
                                                <option value="">Select</option>
                                                <option value="withPay" disabled={!paidLeaveEligible}>
                                                    With Pay
                                                </option>
                                                <option value="withoutPay">
                                                    Without Pay
                                                </option>
                                            </FormControl>
                                            {this.errorMessage(
                                                "paymentType",
                                                "Payment Type can't be empty",
                                            )}
                                            {selectedEmployee && !paidLeaveEligible && (
                                                <div className="form-text text-danger sz-11px">
                                                    Paid leave requires a non-probationary employee with a valid regularization date. Select Without Pay.
                                                </div>
                                            )}
                                            {this.errorMessage(
                                                "paidLeaveEligibility",
                                                "This employee is not eligible for paid leave. Select Without Pay.",
                                            )}
                                        </FormGroup>
                                        {paymentType === "withPay" && (
                                            <FormGroup>
                                                <label>
                                                    <input type="checkbox" checked={this.state.allocationMode} onChange={this.toggleAllocations} />{" "}
                                                    Use multiple leave allocations
                                                </label>
                                            </FormGroup>
                                        )}
                                        {paymentType && !this.state.allocationMode && (
                                            <FormGroup>
                                                <ControlLabel>
                                                    Leave Type
                                                    <span className="required-star">
                                                        *
                                                    </span>
                                                </ControlLabel>
                                                <FormControl
                                                    onChange={
                                                        this.handleFormChange
                                                    }
                                                    componentClass="select"
                                                    name="leaveType"
                                                    value={leaveType}
                                                    disabled={
                                                        paymentType ===
                                                            "withPay" &&
                                                        balanceLoading
                                                    }
                                                >
                                                    <option value="">
                                                        {balanceLoading
                                                            ? "Loading balances..."
                                                            : "Select"}
                                                    </option>
                                                    {paymentType ===
                                                    "withPay" ? (
                                                        PAID_LEAVE_TYPES.map(
                                                            (type) => {
                                                                const balance =
                                                                    leaveBalance &&
                                                                    leaveBalance
                                                                        .balances[
                                                                        type
                                                                            .value
                                                                    ];
                                                                const label =
                                                                    balance
                                                                        ? `${type.label} - ${balance.remaining} left | ${balance.availableToRequest} available for period`
                                                                        : type.label;
                                                                return (
                                                                    <option
                                                                        key={
                                                                            type.value
                                                                        }
                                                                        value={
                                                                            type.value
                                                                        }
                                                                        disabled={
                                                                            balance &&
                                                                            balance.availableToRequest <
                                                                                0.01
                                                                        }
                                                                    >
                                                                        {label}
                                                                    </option>
                                                                );
                                                            },
                                                        )
                                                    ) : (
                                                        UNPAID_LEAVE_TYPES.map(
                                                            (type) => (
                                                                <option
                                                                    key={
                                                                        type.value
                                                                    }
                                                                    value={
                                                                        type.value
                                                                    }
                                                                >
                                                                    {type.label}
                                                                </option>
                                                            ),
                                                        )
                                                    )}
                                                </FormControl>
                                                {this.errorMessage(
                                                    "leaveType",
                                                    "Leave Type can't be empty",
                                                )}
                                                {this.errorMessage(
                                                    "leaveBalance",
                                                    "No paid balance is available for this leave period. Create the request without pay instead.",
                                                )}
                                            </FormGroup>
                                        )}
                                        {paymentType === "withPay" &&
                                            balanceError && (
                                                <Alert bsStyle="warning">
                                                    {balanceError}
                                                </Alert>
                                            )}
                                        {paymentType === "withPay" &&
                                            leaveBalance &&
                                            !this.state.allocationMode &&
                                            !hasAnyPaidBalance && (
                                                <Alert bsStyle="danger">
                                                    No paid leave balance is
                                                    available for this leave
                                                    period. Create the request
                                                    without pay instead.
                                                </Alert>
                                            )}
                                        {paymentType === "withPay" &&
                                            leaveBalance &&
                                            leaveBalance.source &&
                                            !leaveBalance.source
                                                .payLevelConfigured && (
                                                <Alert bsStyle="warning">
                                                    Sick and vacation leave
                                                    allowances have not been
                                                    configured for this
                                                    employee&apos;s pay level.
                                                </Alert>
                                            )}
                                        {paymentType === "withPay" &&
                                            selectedBalance && (
                                                <div className="leave-balance-summary">
                                                    <div>
                                                        <span>
                                                            {leaveBalance.source &&
                                                            leaveBalance.source
                                                                .isProrated
                                                                ? "Pro-rated bank"
                                                                : "Annual bank"}
                                                        </span>
                                                        <strong>
                                                            {
                                                                selectedBalance.initial
                                                            }{" "}
                                                            days
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        <span>
                                                            Monthly entitlement
                                                        </span>
                                                        <strong>
                                                            {
                                                                selectedBalance.monthlyEntitlement
                                                            }{" "}
                                                            days
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        <span>Used / pending</span>
                                                        <strong>
                                                            {
                                                                selectedBalance.used
                                                            }{" "}
                                                            /{" "}
                                                            {
                                                                selectedBalance.pending
                                                            }
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        <span>
                                                            Available for period
                                                        </span>
                                                        <strong>
                                                            {
                                                                selectedBalance.availableToRequest
                                                            }{" "}
                                                            days
                                                        </strong>
                                                    </div>
                                                </div>
                                            )}
                                        {paymentType === "withPay" &&
                                            selectedBalance &&
                                            selectedBalance.availableToRequest >
                                                0 && (
                                                <Alert bsStyle="info">
                                                    Up to{" "}
                                                    {
                                                        selectedBalance.availableToRequest
                                                    }{" "}
                                                    day(s) will be paid. Any
                                                    remaining duration in this
                                                    request will automatically
                                                    be treated as leave without
                                                    pay.
                                                </Alert>
                                            )}
                                        {leaveType === "other" && (
                                            <FormGroup>
                                                <ControlLabel>
                                                    Specify Leave Type
                                                </ControlLabel>
                                                <FormControl
                                                    type="text"
                                                    name="leaveTypeDetail"
                                                    value={leaveTypeDetail}
                                                    onChange={
                                                        this.handleFormChange
                                                    }
                                                    placeholder=""
                                                />
                                            </FormGroup>
                                        )}
                                        <Row>
                                            <div className="col-md-6">
                                                <FormGroup>
                                                    <ControlLabel>
                                                        Start Date
                                                        <span className="required-star">
                                                            *
                                                        </span>
                                                    </ControlLabel>
                                                    <DatePicker
                                                        selected={startDate}
                                                        onChange={(date) =>
                                                            this.onChangeDate(
                                                                date,
                                                                "startDate",
                                                            )
                                                        }
                                                        dateFormat="MM/dd/yyyy"
                                                        className="dateInput"
                                                        dropdownMode="select"
                                                        minDate={minimumLeaveDate}
                                                    />
                                                    {this.errorMessage(
                                                        "startDate",
                                                        "Start Date can't be empty",
                                                    )}
                                                    {this.errorMessage(
                                                        "leaveBeforeRegularization",
                                                        "Start Date cannot be before the employee's regularization date",
                                                    )}
                                                </FormGroup>
                                            </div>
                                            <div className="col-md-6">
                                                <FormGroup>
                                                    <ControlLabel>
                                                        End Date
                                                        <span className="required-star">
                                                            *
                                                        </span>
                                                    </ControlLabel>
                                                    <DatePicker
                                                        selected={endDate}
                                                        minDate={startDate || minimumLeaveDate}
                                                        onChange={(date) =>
                                                            this.onChangeDate(
                                                                date,
                                                                "endDate",
                                                            )
                                                        }
                                                        dateFormat="MM/dd/yyyy"
                                                        className="dateInput"
                                                        dropdownMode="select"
                                                    />
                                                    {this.errorMessage(
                                                        "endDate",
                                                        "End Date can't be empty",
                                                    )}
                                                    {this.errorMessage(
                                                        "endDateBeforeStart",
                                                        "End Date must be on or after Start Date",
                                                    )}
                                                </FormGroup>
                                            </div>
                                        </Row>
                                        {paymentType === "withPay" && this.state.allocationMode && (
                                            <LeaveAllocationEditor
                                                allocations={this.state.resource.leaveAllocations}
                                                balances={leaveBalance && leaveBalance.balances}
                                                onChange={this.setAllocations}
                                                onPreview={this.previewAllocations}
                                                preview={this.state.allocationPreview}
                                                loading={this.state.previewLoading}
                                                error={this.state.previewError}
                                            />
                                        )}
                                    </div>
                                )}
                                {applicationType === "undertime" && (
                                    <div>
                                        <FormGroup>
                                            <ControlLabel>
                                                Date
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </ControlLabel>
                                            <DatePicker
                                                selected={date}
                                                onChange={(date) =>
                                                    this.onChangeDate(
                                                        date,
                                                        "date",
                                                    )
                                                }
                                                dateFormat="MM/dd/yyyy"
                                                className="dateInput"
                                                dropdownMode="select"
                                                maxDate={new Date()}
                                            />
                                            {this.errorMessage(
                                                "date",
                                                "Date can't be empty",
                                            )}
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                From
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </ControlLabel>
                                            <label className="form-helper-text">
                                                In 24 hours format, including
                                                seconds (eg: 13:55:00)
                                            </label>
                                            <FormControl
                                                type="text"
                                                name="timeFrom"
                                                value={timeFrom}
                                                onChange={this.handleFormChange}
                                                placeholder=""
                                            />
                                            {this.errorMessage(
                                                "timeFrom",
                                                "Time from can't be empty",
                                            )}
                                        </FormGroup>
                                        <FormGroup>
                                            <ControlLabel>
                                                To
                                                <span className="required-star">
                                                    *
                                                </span>
                                            </ControlLabel>
                                            <label className="form-helper-text">
                                                In 24 hours format, including
                                                seconds (eg: 13:55:00)
                                            </label>
                                            <FormControl
                                                type="text"
                                                name="timeTo"
                                                value={timeTo}
                                                onChange={this.handleFormChange}
                                                placeholder=""
                                            />
                                            {this.errorMessage(
                                                "timeTo",
                                                "Time to can't be empty",
                                            )}
                                        </FormGroup>
                                    </div>
                                )}
                                <FormGroup>
                                    <ControlLabel>Reason</ControlLabel>
                                    <FormControl
                                        type="text"
                                        name="reason"
                                        value={reason}
                                        onChange={this.handleFormChange}
                                        placeholder=""
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <ControlLabel>
                                        Approved By
                                        <span className="required-star">*</span>
                                    </ControlLabel>
                                    <EmployeeAsyncSelect
                                        onChange={this.handleApproveChange}
                                        placeholder="Approved By"
                                    />
                                    {/* <FormControl
                                        onChange={this.handleFormChange}
                                        componentClass="select"
                                        name="approvedBy"
                                        value={approvedBy}
                                    >
                                        <option value="">Select</option>
                                        {employeeSelectOptions.map((option) => {
                                            return (
                                                <option
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </option>
                                            );
                                        })}
                                    </FormControl> */}
                                    {this.errorMessage(
                                        "approvedBy",
                                        "Approved By can't be empty",
                                    )}
                                </FormGroup>

                                {error && (
                                    <Alert bsStyle="danger">{error}</Alert>
                                )}

                                <Button
                                    bsStyle="primary"
                                    pullRight
                                    fill
                                    type="button"
                                    disabled={this.state.saving || (this.state.allocationMode && (!this.state.allocationPreview || this.state.previewLoading))}
                                    onClick={this.validate}
                                >
                                    {this.state.saving ? "Submitting..." : "Submit Form"}
                                </Button>
                                <div className="clearfix" />
                            </form>
                        }
                    />
                </Col>
            </Row>
        );
    }
}

export default LeaveForm;
